import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { GraduationCap, Award, Briefcase, ArrowRight, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

// Profile image served from same domain to avoid security software blocking
const PROFILE_IMAGE = "/profile-image.jpg";

const education = [
{ institution: "Johns Hopkins University", degree: "Agentic AI Program", icon: GraduationCap },
{ institution: "UT Austin", degree: "AI/ML Program", icon: GraduationCap },
{ institution: "Rutgers University", degree: "MBA - Data Science", icon: GraduationCap },
{ institution: "Computer Science Engineering", degree: "Bachelor's Degree", icon: GraduationCap }];



const certifications = [
"AWS Certified Solutions Architect",
"TOGAF Enterprise Architecture",
"Neo4j Certified Professional"];


const careerHighlights = [
"Senior VP to Director transition leading enterprise AI initiatives",
"Led $100M+ contact center modernization and cloud adoption programs",
"Filed multiple AI patents in conversational AI and automation",
"Built and deployed production-grade LLM/RAG systems",
"Transformed voice AI operations for Fortune 500 companies",
"Pioneered agentic AI implementations in enterprise settings"];


const leadershipPrinciples = [
{ title: "Clarity in Complexity", desc: "Making AI accessible and actionable for business leaders" },
{ title: "Pragmatic Innovation", desc: "Balancing cutting-edge technology with practical implementation" },
{ title: "Outcome-Driven", desc: "Focusing on measurable business impact over technology hype" },
{ title: "Knowledge Sharing", desc: "Empowering teams and organizations through education" }];


export default function AboutPage() {
  return (
    <div className="pt-20" data-testid="about-page">
      {/* Hero Section */}
      <section className="py-24 md:py-32 hero-gradient grain">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}>

              <span className="text-xs font-semibold tracking-[0.2em] uppercase text-royal-blue-800 mb-4 block">
                About
              </span>
              <h1 className="font-display text-4xl md:text-5xl font-bold text-zinc-950 mb-6">
                Building the Future of Enterprise AI
              </h1>
              <p className="!text-lg mb-6 text-zinc-600">I'm Vasanth Natarajan, an AI strategist and enterprise architect with over two decades of experience transforming how organizations leverage artificial intelligence. My journey has taken me from hands-on technical roles to executive leadership, giving me a unique perspective on both the possibilities and practicalities of AI implementation.




              </p>
              <p className="text-zinc-600 leading-relaxed">Today, I help enterprises navigate the fast‑moving world of Generative AI, Agentic Systems, and Contact Center Modernization. My work focuses on making advanced AI approachable and actionable — turning complex capabilities into solutions that drive efficiency, elevate customer experience, and unlock new strategic possibilities.
Whether I’m designing architectures, advising executives, or simplifying AI for broader teams, my mission remains the same:
empower organizations to adopt AI with clarity, confidence, and long‑term impact

              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="flex justify-center lg:justify-end">

              <div className="relative">
                <div className="absolute -inset-4 bg-royal-blue-800/5 rounded-sm"></div>
                <img
                  src={PROFILE_IMAGE}
                  alt="Vasanth - AI Strategist"
                  className="relative profile-image w-72 h-72 md:w-96 md:h-96 object-cover"
                  data-testid="about-profile-image" />

              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Career Highlights Section */}
      <section className="py-24 md:py-32 bg-white" data-testid="career-highlights-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="mb-16">

            <span className="text-xs font-semibold tracking-[0.2em] uppercase text-royal-blue-800 mb-4 block">
              Experience
            </span>
            <h2 className="font-display text-3xl md:text-4xl font-semibold text-zinc-950 mb-4">
              Career Highlights
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {careerHighlights.map((highlight, index) =>
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="flex items-start gap-4 p-6 bg-zinc-50 border border-zinc-200 rounded-sm"
              data-testid={`career-highlight-${index}`}>

                <CheckCircle className="text-royal-blue-800 flex-shrink-0 mt-1" size={20} />
                <p className="text-zinc-700">{highlight}</p>
              </motion.div>
            )}
          </div>
        </div>
      </section>

      {/* Education & Certifications Section */}
      <section className="py-24 md:py-32 section-muted" data-testid="education-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24">
            {/* Education */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}>

              <span className="text-xs font-semibold tracking-[0.2em] uppercase text-royal-blue-800 mb-4 block">
                Education
              </span>
              <h2 className="font-display text-3xl font-semibold text-zinc-950 mb-8">
                Academic Foundation
              </h2>
              <div className="space-y-6">
                {education.map((edu, index) =>
                <div
                  key={index}
                  className="flex items-start gap-4 p-6 bg-white border border-zinc-200 rounded-sm"
                  data-testid={`education-item-${index}`}>

                    <div className="skill-icon">
                      <edu.icon size={24} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-zinc-950">{edu.institution}</h3>
                      <p className="text-zinc-600">{edu.degree}</p>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>

            {/* Certifications */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}>

              <span className="text-xs font-semibold tracking-[0.2em] uppercase text-royal-blue-800 mb-4 block">
                Certifications
              </span>
              <h2 className="font-display text-3xl font-semibold text-zinc-950 mb-8">
                Professional Credentials
              </h2>
              <div className="space-y-4">
                {certifications.map((cert, index) =>
                <div
                  key={index}
                  className="flex items-center gap-4 p-6 bg-white border border-zinc-200 rounded-sm"
                  data-testid={`certification-item-${index}`}>

                    <Award className="text-royal-blue-800" size={24} />
                    <span className="font-medium text-zinc-950">{cert}</span>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Leadership Philosophy Section */}
      <section className="py-24 md:py-32 bg-white" data-testid="leadership-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="mb-16 text-center">

            <span className="text-xs font-semibold tracking-[0.2em] uppercase text-royal-blue-800 mb-4 block">
              Philosophy
            </span>
            <h2 className="font-display text-3xl md:text-4xl font-semibold text-zinc-950 mb-4">
              Leadership Principles
            </h2>
            <p className="text-lg text-zinc-600 max-w-2xl mx-auto">
              Guiding principles that shape how I approach AI strategy and transformation.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {leadershipPrinciples.map((principle, index) =>
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="p-8 bg-zinc-50 border border-zinc-200 rounded-sm"
              data-testid={`principle-${index}`}>

                <h3 className="font-display text-xl font-semibold text-zinc-950 mb-3">
                  {principle.title}
                </h3>
                <p className="text-zinc-600">
                  {principle.desc}
                </p>
              </motion.div>
            )}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 cta-section" data-testid="about-cta-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}>

            <h2 className="font-display text-3xl md:text-4xl font-semibold mb-6">
              Ready to Transform Your Enterprise?
            </h2>
            <p className="text-blue-100 text-lg mb-8 max-w-xl mx-auto">
              Let's discuss how AI can drive real value for your organization.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/contact">
                <Button
                  className="bg-white text-royal-blue-800 hover:bg-blue-50 px-8 py-6 text-xs tracking-wider uppercase"
                  data-testid="about-contact-btn">

                  Get in Touch
                  <ArrowRight className="ml-2" size={16} />
                </Button>
              </Link>
              <Link to="/lets-talk-ai">
                <Button
                  variant="outline"
                  className="border-white text-white hover:bg-white/10 px-8 py-6 text-xs tracking-wider uppercase"
                  data-testid="about-lets-talk-btn">

                  Let's Talk AI
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>);

}